/**
 * @author Harish <harish.r@314ecorp.com>
 * @description AssigneesList
 */
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Search, Plus, Write } from '@icon-park/react';
import {
	Avatar,
	Button,
	Checkbox,
	Dropdown,
	DropDownProps,
	Empty,
	Flex,
	Input,
	List,
	MenuProps,
	Skeleton,
	Space,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import { INTERNAL_ADMIN_ROLE, ANALYTICS_USER_ROLE } from 'configs/index';
import { AccessControl } from 'components/widgets';
import { findUserRole, RoleBadge } from 'components/userManagement/UserBadge';
import useKeycloakUsers from 'hooks/admin/useKeycloakUsers';

/**
 * prepareAssignableUsersMap
 */
const prepareAssignableUserMeta = ({
	users,
	excludeRoles,
	activeOnly,
}: {
	users: UserManagement.IUserResponseModel[];
	excludeRoles?: string[];
	activeOnly?: boolean;
}): Record<string, UserManagement.IAssignableUserMeta> => {
	const userMeta = {};
	_.forEach(users, (user) => {
		// if we need only active users
		if (activeOnly && !user?.enabled) {
			return;
		}
		// if exclude roles are there
		if (!_.isEmpty(excludeRoles)) {
			const userRole = findUserRole(user?.roles);
			if (userRole && _.includes(excludeRoles, userRole)) {
				return;
			}
		}
		const name = _.trim(`${user?.firstName || ''} ${user?.lastName || ''}`) || 'Unknown';
		userMeta[user.id] = {
			id: user?.id,
			roles: user?.roles,
			name,
			email: user?.email,
			isActive: user?.enabled,
			avatar: _.trim(_.upperCase(`${_.first(user?.firstName) || ''}${_.first(user?.lastName) || ''}`)) || 'U',
		};
	});
	return userMeta;
};

const sortByActiveStatus = (users: UserManagement.IAssignableUserMeta[]): UserManagement.IAssignableUserMeta[] => {
	return _.sortBy(users, (user) => (user?.isActive ? 0 : 1));
};

interface IProps {
	users: UserManagement.IUserMeta[];
	loading?: boolean;
	disableSearch?: boolean;
	maxLength?: number;
	showEmptyTag?: boolean;
}

const AvatarGroup: React.FC<IProps> = ({ users, loading, disableSearch, maxLength = 5, showEmptyTag = true }) => {
	const [search, setSearch] = useState('');

	const getTitle = (user: UserManagement.IUserMeta) => (user?.isActive ? user?.name : `${user?.name} (Deactivated User)`);
	const getAvatar = (user: UserManagement.IUserMeta) => {
		return (
			<Tooltip key={user?.email} title={getTitle(user)}>
				<Avatar
					className={`flex cursor-default items-center border border-white ${user?.isActive ? 'bg-avatar-background' : 'bg-[#E6F4FF] text-[#5FA8E6]'}`}
				>
					{user?.avatar}
				</Avatar>
			</Tooltip>
		);
	};

	const getPopoverContent = () => {
		if (!disableSearch) {
			return (
				<Space direction='vertical' className='p-2.5'>
					<Input
						placeholder='Search'
						suffix={<Search />}
						onClick={(e) => e.stopPropagation()}
						onChange={(e) => setSearch(e.target.value)}
					/>

					<List
						className='max-h-62.5 overflow-auto'
						dataSource={_.filter(users, ({ name }) => _.includes(_.toLower(name), _.toLower(search)))}
						renderItem={(user) => (
							<List.Item key={user?.email} className='border-none p-1'>
								<Space>
									{getAvatar(user)}
									<Typography.Text>{getTitle(user)}</Typography.Text>
								</Space>
							</List.Item>
						)}
					/>
				</Space>
			);
		}
		return null;
	};

	if (loading) {
		return <Skeleton.Button active />;
	}

	if (!_.isEmpty(users)) {
		return (
			<Avatar.Group
				max={{
					count: maxLength,
					style: { backgroundColor: '#F2F4F7', color: '#344054', cursor: 'pointer' },
					popover: { content: getPopoverContent(), styles: { body: { padding: 0 } } },
				}}
			>
				{_.map(users, getAvatar)}
			</Avatar.Group>
		);
	}

	if (showEmptyTag) {
		return (
			<Tag color='default' className='w-fit'>
				{'Unassigned'}
			</Tag>
		);
	}
	return null;
};

/**
 * AssigneesList
 */
const AssigneesList: React.FC<{ assignedUsers: string[]; maxLength?: number; showEmptyTag?: boolean }> = ({
	assignedUsers,
	maxLength = 4,
	showEmptyTag = true,
}) => {
	const { data, isLoading } = useKeycloakUsers({ allUsers: true });
	const meta = useMemo(() => prepareAssignableUserMeta({ users: data?.data }), [data?.data]);
	const users = useMemo(() => _.compact(_.map(assignedUsers, (userID) => _.get(meta, userID))), [meta, assignedUsers]);
	const sortedUsers = sortByActiveStatus(users);
	return <AvatarGroup loading={isLoading} users={sortedUsers} maxLength={maxLength} showEmptyTag={showEmptyTag} />;
};

/**
 * New Assignees List
 */

interface IAddAssignee extends DropDownProps {
	value: string[];
	onChange: (value: string[]) => void;
	showOnlyIcon?: boolean;
	showEmptyTag?: boolean;
}

const AddAssignee: React.FC<IAddAssignee> = ({
	onChange,
	value,
	showOnlyIcon = false,
	showEmptyTag = true,
	overlayClassName,
	...dropdownProps
}) => {
	const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
	const [search, setSearch] = useState('');
	const [open, setOpen] = useState(false);

	const { data, isLoading } = useKeycloakUsers({ allUsers: true });
	const meta = useMemo(
		() =>
			prepareAssignableUserMeta({
				users: data?.data,
				excludeRoles: [ANALYTICS_USER_ROLE, INTERNAL_ADMIN_ROLE],
			}),
		[data?.data],
	);
	const selectedUsers = useMemo(
		() => sortByActiveStatus(_.compact(_.map(selectedOptions, (userID) => _.get(meta, userID)))),
		[meta, selectedOptions],
	);
	const selectedInactiveUsersId = _.compact(_.map(selectedUsers, (user) => !user?.isActive && user?.id));
	const metaList = useMemo(
		() => sortByActiveStatus(_.filter(_.values(meta), (item) => item?.isActive || _.includes(selectedInactiveUsersId, item?.id))),
		[meta, selectedInactiveUsersId],
	);

	// Filter users based on search text in the UI
	const filteredUsers = useMemo(() => {
		if (!search) {
			return metaList;
		}
		return _.filter(
			metaList,
			(user) => _.includes(_.toLower(user?.email), _.toLower(search)) || _.includes(_.toLower(user?.name), _.toLower(search)),
		);
	}, [metaList, search]);

	useEffect(() => {
		if (!_.isEmpty(value)) {
			setSelectedOptions(value);
		}
	}, [value]);

	const handleOptionClick = (optionId: string) => {
		setSelectedOptions((prevSelected) => {
			if (_.includes(prevSelected, optionId)) {
				return _.filter(prevSelected, (id) => id !== optionId);
			} else {
				return [...prevSelected, optionId];
			}
		});
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
	};

	const popupRender = (menu: React.ReactNode) => (
		<div>
			<div className='sticky top-0 z-1 bg-white p-2'>
				<Input
					placeholder='Search Users'
					suffix={<Search />}
					onClick={(e) => e.stopPropagation()}
					onChange={(e) => handleSearch(e)}
					value={search}
				/>
			</div>
			{_.isEmpty(filteredUsers) ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : menu}
		</div>
	);

	const dropdownItems: MenuProps['items'] = _.map(filteredUsers, (option) => ({
		key: option.id,
		label: (
			<Flex
				align='center'
				justify='space-between'
				gap={24}
				onClick={(e) => {
					e.stopPropagation();
					handleOptionClick(option.id);
				}}
				onKeyDown={(e) => e.stopPropagation()}
				className='border-b border-[#e8e8e8] px-3 py-1.25'
			>
				<Flex gap={8}>
					<Checkbox checked={_.includes(selectedOptions, option.id)} />
					<div>
						<span>{option.name}</span>
						<br />
						<span>{option.email}</span>
					</div>
				</Flex>
				{option?.isActive ? <RoleBadge role={findUserRole(option.roles)} mode='tag' /> : <Tag>{'Deactivated User'}</Tag>}
			</Flex>
		),
	}));

	const getButton = (iconOnly: boolean) => {
		const buttonProps = {
			icon: iconOnly ? <Plus /> : undefined,
			onClick: () => setOpen(true),
			disabled: open,
		};
		if (iconOnly) {
			return <Button {...buttonProps} type='dashed' />;
		}
		return <Button {...buttonProps}>{'Add Assignee(s)'}</Button>;
	};

	const handleOpenChange = (flag: boolean) => {
		setOpen(flag);
		if (!flag) {
			onChange(selectedOptions);
		}
	};

	return (
		<div>
			<Dropdown
				open={open}
				onOpenChange={handleOpenChange}
				menu={{ items: dropdownItems }}
				trigger={['click']}
				placement='bottomRight'
				getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
				popupRender={popupRender}
				overlayClassName={`add-assignee-dropdown-overlay ${overlayClassName}`}
				{...(dropdownProps || {})}
			/>
			<AccessControl permission={'canAssignWorklists'}>{_.isEmpty(selectedOptions) && getButton(showOnlyIcon)}</AccessControl>
			{!_.isEmpty(selectedOptions) && (
				<div className='flex items-center gap-2'>
					{' '}
					<AvatarGroup users={selectedUsers} loading={isLoading} maxLength={10} showEmptyTag={showEmptyTag} />
					<AccessControl permission={'canAssignWorklists'}>{getButton(true)}</AccessControl>
				</div>
			)}
		</div>
	);
};

interface IEditableFieldProps {
	value: string;
	onSave: (value: string) => void;
	fieldType: string;
	label?: string;
	icon?: React.ReactNode;
	maxLength?: number;
}

const ExpandableText: React.FC<{ children: string; maxLength?: number }> = ({ children, maxLength = 100 }) => {
	const text = children;

	const [isExpanded, setIsExpanded] = useState(false);

	if (typeof text !== 'string' || text?.length <= maxLength) {
		return <Typography.Text>{text}</Typography.Text>;
	}

	const toggleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	const displayedText = isExpanded ? text : `${text?.slice(0, maxLength)}...`;
	const buttonText = isExpanded ? 'Show less' : 'Show more';

	return (
		<span>
			<Typography.Text>{displayedText}</Typography.Text>
			<Button type='link' onClick={toggleExpand} className='ml-2 h-auto p-0 leading-[inherit]'>
				{buttonText}
			</Button>
		</span>
	);
};

const componentMap = {
	title: {
		key: 'title',
		edit: Input.TextArea,
		view: (props: AnyRecord): React.ReactNode => <Typography.Title level={4} className='m-0' {...props} />,
	},
	description: {
		key: 'description',
		edit: Input.TextArea,
		view: ExpandableText,
	},
};

const EditableField: React.FC<IEditableFieldProps> = ({ value, onSave, fieldType, label, icon = <Write />, maxLength }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [inputValue, setInputValue] = useState(value);

	const ViewComponent = _.find(componentMap, { key: fieldType })?.view;
	const EditComponent = _.find(componentMap, { key: fieldType })?.edit;

	const handleEdit = () => {
		setIsEditing(true);
	};

	const handleSave = () => {
		onSave(inputValue);
		setIsEditing(false);
	};

	return (
		<Flex vertical gap={4}>
			{label && (
				<Flex align='center'>
					<span className='text-neutral-8'>{label}</span>
					{!isEditing && <Button variant='link' color='default' className='h-auto' icon={icon} onClick={handleEdit} />}
				</Flex>
			)}
			{isEditing ? (
				<Flex gap={8}>
					{EditComponent && (
						<EditComponent value={inputValue} onChange={(e) => setInputValue(e.target.value)} onBlur={handleSave} autoFocus />
					)}
				</Flex>
			) : (
				<Flex justify='space-between' align='center'>
					{ViewComponent && <ViewComponent maxLength={maxLength}>{value}</ViewComponent>}
				</Flex>
			)}
		</Flex>
	);
};

export { AssigneesList, AddAssignee, ExpandableText, EditableField, componentMap, prepareAssignableUserMeta };
